import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

// Written by whichever deploy the browser last visited, so a value of another
// shape is expected rather than a fault: it reads as unset, unreported.
const storedCheckboxValue = z.boolean().nullable();

type Remembered = {
  // False until the stored value has been read, which happens after mount so
  // that a prerendered checkbox hydrates as the server rendered it.
  isRead: boolean;
  remember: (value: boolean) => void;
};

// Keeps a checkbox's value across visits under `key`. The stored value is
// handed to `restore` once after mount, and only when there is one.
export function useRememberedCheckbox(
  key: string,
  restore: (value: boolean) => void
): Remembered {
  const [isRead, setIsRead] = useState(false);

  useEffect(() => {
    const value = readCheckboxValue(key);
    if (value !== null) {
      restore(value);
    }
    setIsRead(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reads once on mount
  }, []);

  const remember = useCallback(
    (value: boolean) => writeCheckboxValue(key, value),
    [key]
  );

  return { isRead, remember };
}

function writeCheckboxValue(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // We end up here when the user local storage quota is full or user private browses on mobile Safari.
  }
}

function readCheckboxValue(key: string): boolean | null {
  let parsedData = null;
  try {
    const data = localStorage.getItem(key);
    if (data) {
      parsedData = JSON.parse(data);
    }
  } catch (_err) {
    // We don't care if localStorage.getItem or JSON.parse throws error
  }
  const result = storedCheckboxValue.safeParse(parsedData);
  return result.success ? result.data : null;
}
